import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ComboBox } from '@/components/ComboBox';
import { useToast } from '@/components/Toast';
import { TemplateControls } from '@/components/TemplateControls';
import {
  useCreateEngagement, useLookup, useTemplateControls, useClientGroupings, useClientPicker,
  type ClientPickerRow, type TemplateControl,
} from '@/api/hooks';
import { api, ApiError } from '@/api/client';
import { Section, Field, Warn, LookupCombo, EMAIL_RE } from '@/components/FormKit';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
type Row = Record<string, unknown>;

// CAD Cleanup B1 — the Deliverables box is removed on New Engagement for ALL templates
// EXCEPT these (Tyler: "remove from most engagements, collect via Other Details"). Every
// hidden template keeps its single default-checked deliverable (seeded below), so the
// primary Maconomy form flag is still submitted. Kept where the box still carries needed
// data / can't be safely defaulted:
//  - Form 1099 / International Tax Consulting: their only deliverable is NOT default-checked,
//    so hiding the box would submit nothing.
// (Property Tax's box is now hidden too — its counties field was pulled out into its own
//  always-shown required field, so the box no longer holds anything the form needs.)
const KEEP_DELIVERABLES_TEMPLATES = new Set(['Form 1099', 'International Tax Consulting']);

// CAD Cleanup C4 (Foreign Corporation Tax, tab Z21) — "State Incorporated" is rendered as a
// free-text box (country of incorporation) instead of the shared US-states dropdown.
const FOREIGN_CORP = 'Foreign Corporation Tax';
const COUNTRY_TEXT_KEYS = new Set(['StateIncorporated']);

// CAD Cleanup — controls hidden per template in the web app only (shared config + Power App
// untouched). Amanda: Fiduciary Name/Title don't apply to gift (709) returns, remove them.
const HIDE_CONTROLS_BY_TEMPLATE: Record<string, string[]> = {
  'Gift Tax': ['FiduciaryName', 'FiduciaryTitle'],
  'Gift Tax Dec': ['FiduciaryName', 'FiduciaryTitle'],
};

// CAD Cleanup C1 (Individual 1040, tab Y30) — the spouse Occupation + Date of Birth are
// required ONLY when a spouse is actually entered. The form has no dedicated spouse-name
// field, so "a spouse is entered" is inferred from any spouse field carrying a value.
const SPOUSE_TRIGGER_KEYS = ['DateOfBirthSpouse', 'DateDeceasedSpouse', 'OccupationSpouse'];
const SPOUSE_REQUIRED_KEYS = new Set(['OccupationSpouse', 'DateOfBirthSpouse']);

const EMPTY_DETAILS: Record<string, string> = {
  practiceAreaPartner: '', taxSigner: '', engagementManager: '', inCharge: '',
  engagementLetterFee: '', engagementLocation: '', emailForInvoice: '',
  emailForMonthlyStatements: '', elSignerName: '', elSignerEmail: '', notes: '',
};

export function NewEngagement() {
  const navigate = useNavigate();
  const notify = useToast();
  const create = useCreateEngagement();
  const [confirm, setConfirm] = useState(false);
  // true when the pending submit is "Submit & Duplicate" (stay on a fresh form)
  const [duplicate, setDuplicate] = useState(false);

  // header
  const [serviceCategory, setServiceCategory] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [year, setYear] = useState('');
  const [fiscalYearEndMonth, setFiscalYearEndMonth] = useState('');
  const [recurringAnnually, setRecurringAnnually] = useState(''); // '' | 'Yes' | 'No'
  // client
  const [grouping, setGrouping] = useState('');
  const [client, setClient] = useState<ClientPickerRow | null>(null);
  // details
  const [d, setD] = useState<Record<string, string>>({ ...EMPTY_DETAILS });
  const setField = (k: string, v: string) => setD((s) => ({ ...s, [k]: v }));
  // section 2 state
  const [deliverables, setDeliverables] = useState<Record<string, boolean>>({});
  const [otherDetails, setOtherDetails] = useState<Record<string, string>>({});
  const [counties, setCounties] = useState('');

  // lookups
  const categories = useLookup('service-categories');
  const years = useLookup('fiscal-years');
  const partners = useLookup('practice-area-partners');
  const managers = useLookup('engagement-managers');
  const signers = useLookup('tax-signers');
  const inCharge = useLookup('in-charge');
  const templates = useQuery({
    queryKey: ['templates', serviceCategory],
    queryFn: () => api.get<Row[]>(`/lookups/templates?serviceCategory=${encodeURIComponent(serviceCategory)}`),
    enabled: !!serviceCategory,
  });
  const groupings = useClientGroupings();
  const clientPicker = useClientPicker(grouping || undefined, true);

  // Engagement Location auto-derives from the Practice Area Partner (Power Apps: vw_EmployeeEngLocation).
  const engLocation = useQuery({
    queryKey: ['eng-location', d.practiceAreaPartner],
    queryFn: () => api.get<{ EngLocation: string }[]>(`/lookups/engagement-locations?partner=${encodeURIComponent(d.practiceAreaPartner)}`),
    enabled: !!d.practiceAreaPartner,
  });
  useEffect(() => {
    setField('engagementLocation', d.practiceAreaPartner ? String(engLocation.data?.[0]?.EngLocation ?? '') : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engLocation.data, d.practiceAreaPartner]);

  // DB-driven template controls (Section 2)
  const controlsQ = useTemplateControls(serviceCategory || undefined, templateName || undefined);
  const controls = controlsQ.data ?? [];
  useEffect(() => {
    if (!controls.length) { setDeliverables({}); setOtherDetails({}); setCounties(''); return; }
    const init: Record<string, boolean> = {};
    for (const c of controls) if (c.ControlType === 'Deliverable') init[c.ControlKey] = c.IsDefaultChecked;
    setDeliverables(init);
    setOtherDetails({});
    setCounties('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlsQ.data]);

  function pickClient(c: ClientPickerRow | null) {
    setClient(c);
    // Defaults pulled from the client record (editable), like Power Apps:
    //  - Fiscal Year End Month
    //  - Email for Monthly Statements <- client's BillingStatementEmail
    if (c?.FiscalYearEndMonth) setFiscalYearEndMonth(String(c.FiscalYearEndMonth));
    setField('emailForMonthlyStatements', c?.BillingStatementEmail ? String(c.BillingStatementEmail) : '');
  }

  // Clear the whole form back to its initial state (for "Submit & Duplicate").
  function resetForm() {
    setServiceCategory(''); setTemplateName(''); setYear(''); setFiscalYearEndMonth(''); setRecurringAnnually('');
    setGrouping(''); setClient(null);
    setD({ ...EMPTY_DETAILS });
    setDeliverables({}); setOtherDetails({}); setCounties('');
  }

  // validation
  const invoiceBad = !!d.emailForInvoice && !EMAIL_RE.test(d.emailForInvoice);
  const monthlyBad = !!d.emailForMonthlyStatements && !EMAIL_RE.test(d.emailForMonthlyStatements);
  const elEmailBad = !!d.elSignerEmail && !EMAIL_RE.test(d.elSignerEmail);
  const hideDeliverables = !!templateName && !KEEP_DELIVERABLES_TEMPLATES.has(templateName);
  const isTaxCompliance = serviceCategory.startsWith('2000');
  const anyDeliverable = Object.values(deliverables).some(Boolean);
  // When the box is hidden the default deliverable is still seeded, so there's nothing to
  // require the user to check — skip the "must pick a deliverable" gate for those templates.
  const deliverableMissing = !hideDeliverables && !!serviceCategory && !!templateName && isTaxCompliance && !anyDeliverable;
  // Counties is now its own always-shown required field on any template that has it (Property Tax).
  const needsCounties = controls.some((c) => c.ControlKey === 'PropertyTaxCountries');
  const propertyTaxMissing = needsCounties && !counties.trim();
  // C1: on 1040, the spouse Occupation/DOB become required only once a spouse is entered;
  // otherwise fall back to each control's own IsRequired flag.
  const spouseEntered =
    templateName === 'Individual Tax' &&
    SPOUSE_TRIGGER_KEYS.some((k) => String(otherDetails[k] ?? '').trim() !== '');
  const requiredFor = (c: TemplateControl) =>
    templateName === 'Individual Tax' && SPOUSE_REQUIRED_KEYS.has(c.ControlKey) ? spouseEntered : c.IsRequired;
  const hideKeys = new Set(HIDE_CONTROLS_BY_TEMPLATE[templateName] ?? []);
  const missingRequiredDetail = controls.some(
    (c) => c.IsEditable && requiredFor(c) && c.ControlType !== 'Deliverable' && !hideKeys.has(c.ControlKey) && !String(otherDetails[c.ControlKey] ?? '').trim(),
  );

  const invalid =
    !serviceCategory || !templateName || !year || !fiscalYearEndMonth || !client ||
    !d.practiceAreaPartner || !d.taxSigner || !d.engagementManager || !d.engagementLetterFee?.trim() ||
    !d.emailForInvoice?.trim() || !d.emailForMonthlyStatements?.trim() ||
    !d.elSignerName?.trim() || !d.elSignerEmail?.trim() ||
    invoiceBad || monthlyBad || elEmailBad ||
    deliverableMissing || propertyTaxMissing || missingRequiredDetail;

  const canAddDeliverables = !!serviceCategory && !!templateName;

  async function submit() {
    setConfirm(false);
    const body = {
      serviceCategory, templateName, year: Number(year), fiscalYearEndMonth, recurringAnnually,
      clientId: String(client?.ClientID ?? ''),
      clientName: String(client?.ClientName ?? ''),
      clientManager: String(client?.ClientManager ?? ''),
      brcIndustry: String(client?.BRCIndustry ?? ''),
      ...d,
      deliverables, propertyTaxCountries: counties, otherDetails,
    };
    try {
      await create.mutateAsync(body);
      if (duplicate) {
        // Power Apps: save, toast, and reload a fresh form to enter another engagement.
        notify('success', 'Engagement saved successfully. You can now enter another Engagement.');
        resetForm();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        notify('success', 'Engagement request submitted.');
        navigate('/engagements');
      }
    } catch (e) {
      notify('error', e instanceof ApiError ? e.problem.title : 'Submit failed');
    }
  }

  return (
    <div className="w-full space-y-4 p-5">
      <div className="columns-1 gap-4 xl:columns-2 [&>*]:mb-4 [&>*]:break-inside-avoid">
        <Section title="Engagement">
          <Field label="Service Category *">
            <LookupCombo data={categories.data} field="ServiceCategory" value={serviceCategory}
              onChange={(v) => { setServiceCategory(v); setTemplateName(''); }} placeholder="Select category…" />
          </Field>
          <Field label="Template Name *">
            <LookupCombo data={templates.data as Row[]} field="TemplateName" value={templateName}
              onChange={setTemplateName} placeholder="Select template…" disabled={!serviceCategory} />
          </Field>
          <Field label="Fiscal Year End *">
            <LookupCombo data={years.data} field="FiscalYear" value={year}
              onChange={setYear} placeholder="Select year…" />
          </Field>
          <Field label="Fiscal Year End Month *">
            <select className="input" value={fiscalYearEndMonth} onChange={(e) => setFiscalYearEndMonth(e.target.value)}>
              <option value="">— select —</option>{MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </Field>
          <Field label="Recurring Annually">
            <select className="input" value={recurringAnnually} onChange={(e) => setRecurringAnnually(e.target.value)}>
              <option value="">— select —</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </Field>
          <p className="col-span-full text-xs text-muted-foreground">
            Select a <b>Service Category</b> and <b>Template Name</b> to load the deliverables &amp; details below.
          </p>
        </Section>

        <Section title="Client">
          <Field label="Client starts with *">
            <LookupCombo data={groupings.data} field="ClientGrouping" value={grouping}
              onChange={(v) => { setGrouping(v); setClient(null); }} placeholder="Select…" />
          </Field>
          <Field label="Client ID *">
            <ComboBox<ClientPickerRow>
              items={clientPicker.data ?? []}
              value={client}
              onChange={pickClient}
              getLabel={(c) => c.ClientFullName ?? `${c.ClientID}-${c.ClientName}`}
              getKey={(c) => c.ClientFullName ?? `${c.ClientID}-${c.ClientName}`}
              placeholder="Search by ID or name…"
              disabled={!grouping}
              loading={clientPicker.isFetching}
            />
          </Field>
          <Field label="Client Name"><input className="input" value={String(client?.ClientName ?? '')} disabled /></Field>
          <Field label="Contact Name"><input className="input" value={String(client?.ContactName ?? '')} disabled /></Field>
          <Field label="Client Email"><input className="input" value={String(client?.ClientEmail ?? '')} disabled /></Field>
          <Field label="Client Manager"><input className="input" value={String(client?.ClientManager ?? '')} disabled /></Field>
          <Field label="BRC Industry"><input className="input" value={String(client?.BRCIndustry ?? '')} disabled /></Field>
        </Section>

        <Section title="Assignments">
          <Field label="Practice Area Partner *">
            <LookupCombo data={partners.data} field="Name" value={d.practiceAreaPartner}
              onChange={(v) => setField('practiceAreaPartner', v)} placeholder="Select partner…" />
          </Field>
          <Field label="Engagement Location">
            <input className="input" value={d.engagementLocation} disabled placeholder="Set by Practice Area Partner" />
          </Field>
          <Field label="Tax Signer *">
            <LookupCombo data={signers.data} field="DisplayText" value={d.taxSigner}
              onChange={(v) => setField('taxSigner', v)} placeholder="Select signer…" />
          </Field>
          <Field label="Engagement Manager *">
            <LookupCombo data={managers.data} field="EngagementManager" value={d.engagementManager}
              onChange={(v) => setField('engagementManager', v)} placeholder="Select manager…" />
          </Field>
          <Field label="In Charge">
            <LookupCombo data={inCharge.data} field="InCharge" value={d.inCharge}
              onChange={(v) => setField('inCharge', v)} placeholder="Select…" />
          </Field>
        </Section>

        <Section title="Billing & Engagement Letter">
          <Field label="Engagement Letter Fee *">
            <input className="input" value={d.engagementLetterFee} onChange={(e) => setField('engagementLetterFee', e.target.value)} />
          </Field>
          <Field label={<>Email for Invoice * <Warn show={invoiceBad} tip="Enter a valid email" /></>}>
            <input className="input" value={d.emailForInvoice} onChange={(e) => setField('emailForInvoice', e.target.value)} />
          </Field>
          <Field label={<>Email for Monthly Statements * <Warn show={monthlyBad} tip="Enter a valid email" /></>}>
            <input className="input" value={d.emailForMonthlyStatements} onChange={(e) => setField('emailForMonthlyStatements', e.target.value)} />
          </Field>
          <Field label="EL Signer Name *">
            <input className="input" value={d.elSignerName} onChange={(e) => setField('elSignerName', e.target.value)} />
          </Field>
          <Field label={<>EL Signer Email * <Warn show={elEmailBad} tip="Enter a valid email" /></>}>
            <input className="input" value={d.elSignerEmail} onChange={(e) => setField('elSignerEmail', e.target.value)} />
          </Field>
        </Section>
      </div>

      <div>
        <label className="label">Notes</label>
        <textarea className="input" rows={2} value={d.notes} onChange={(e) => setField('notes', e.target.value)} />
      </div>

      {/* Section 2 — DB-driven deliverables & other details */}
      {!canAddDeliverables ? null : controlsQ.isFetching ? (
        <div className="card text-sm text-muted-foreground">Loading template controls…</div>
      ) : (
        <TemplateControls
          controls={controls}
          deliverables={deliverables}
          setDeliverable={(k, v) => setDeliverables((s) => ({ ...s, [k]: v }))}
          otherDetails={otherDetails}
          setOtherDetail={(k, v) => setOtherDetails((s) => ({ ...s, [k]: v }))}
          propertyTaxCountries={counties}
          setPropertyTaxCountries={setCounties}
          clientId={client ? String(client.ClientID) : undefined}
          hideDeliverables={hideDeliverables}
          requiredFor={requiredFor}
          textKeys={templateName === FOREIGN_CORP ? COUNTRY_TEXT_KEYS : undefined}
          hideKeys={hideKeys}
        />
      )}

      <p className="text-xs font-semibold text-accent">*  Mandatory Fields</p>

      <div className="flex flex-wrap gap-2">
        <button className="btn-primary" disabled={invalid || create.isPending}
          onClick={() => { setDuplicate(false); setConfirm(true); }}>Submit</button>
        <button className="btn-secondary" disabled={invalid || create.isPending}
          onClick={() => { setDuplicate(true); setConfirm(true); }}>Submit &amp; Duplicate</button>
      </div>

      <ConfirmDialog open={confirm}
        message="Do you wish to submit the engagement form? Press 'Yes' to continue, 'No' to cancel."
        onConfirm={submit} onCancel={() => setConfirm(false)} />
    </div>
  );
}
